import React from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import queryString from 'query-string';
import type { Location } from 'history';
import HomePageSkeleton from './HomePageSkeleton';
import UpdatePaymentMethodsPage from '../UpdatePaymentMethodsPage';
import ManagePaymentMethodsPage from '@renter-hub/pages/ManagePaymentMethods';
import ConfirmRentDetailsPage from '../ConfirmRentDetailsPage';
import PaymentReportingOptInPage from '../PaymentReportingOptInPage';
import PaymentInfoPageV2 from '../PaymentInfoPage';
import PaymentSettingsPageV2 from '../PaymentSettingsPage';
import MoveInCostsSchedulingPage from '../MoveInCostsSchedulingPage';
import RenterDashboardV2 from '../Dashboard';
import ConfirmNewRentPage from '../ConfirmNewRentPage';
import PayOneTimePaymentPage from '../PayOneTimePaymentPage';
import CancelPaymentPage from '../CancelPaymentPage';
import LandingPage from '../LandingPage';
import PayPastDueRentPaymentPage from '../PayPastDueRentPaymentPage';
import PlaidConnectPage from '../PlaidConnectPage';
import OnboardingStatus from '@renter-hub/constants/onboardingStatus';
import { shouldRedirectToYourRental } from './shouldRedirectToYourRental';
import REQUEST_STATUS from '@common/constants/requestStatus';
import paths from '@renter-hub/pathsManifest';
import type {
  RenterConfigInterface,
  RenterMoveInCostsDetailsInterface,
  RenterNextPayableInterface,
  RenterPaymentDetailsInterface,
  RenterPaymentHistoryInterface,
  RenterRentTermsInterface,
} from '@renter-hub/models/Types';
import { findMoveInCostsPaymentTransactions } from '@common/paymentsUtils/moveInCostsUtil';
import { getPaymentMethodStatuses } from '@common/paymentsUtils/multiplePaymentMethodsUtil';
import TransactionConstants from '@common/constants/TransactionConstants';
import { RouteWithAuth } from '@common/components/RouteWithAuth';
import { VERIFICATION_STATUS } from '@common/constants/PlaidRequirement';
interface HomePageProps {
  brand: string;
  propertyId?: string;
  listing?: any;
  requestStatus?: string;
  onboardingStatus?: string;
  onboardingComplete: boolean;
  bankAccountVerificationNeeded?: boolean;
  ignoreRentAmountChange?: boolean;
  location: Location;
  config: RenterConfigInterface;
  unpaidOtpIndexes: any;
  pastDueRentPaymentIndexes: Array<string>;
  nextPayable: RenterNextPayableInterface;
  paymentDetails: RenterPaymentDetailsInterface;
  rentTerms: RenterRentTermsInterface;
  requestRenterStatus: () => any;
  paymentHistory: RenterPaymentHistoryInterface;
  moveInCostsDetails: RenterMoveInCostsDetailsInterface;
  redirectUrl: string;
  fromWhere: string;
  paymentMethods: Array<any>;
}

interface HomePageState {
  requestStatus: string | null;
}

interface PageProps {
  brand: string;
  baseUrl: string;
  listing: any;
  propertyId: string | undefined;
  onboardingStatus: string | undefined;
  onboardingComplete: boolean;
}

class HomePage extends React.Component<HomePageProps, HomePageState> {
  schedulePaymentFlow = false;
  landingPageVisited = false;
  pageProps: PageProps;

  constructor(props: Readonly<HomePageProps>) {
    super(props);

    this.pageProps = {
      brand: props.brand,
      baseUrl: paths.baseUrl,
      listing: props.listing,
      propertyId: props.propertyId,
      onboardingStatus: props.onboardingStatus,
      onboardingComplete: props.onboardingComplete,
    };

    this.state = {
      requestStatus: null,
    };
  }

  isMoveInCostsPresent = () => {
    const { transactions } = this.props.paymentHistory;
    const moveInCosts = findMoveInCostsPaymentTransactions(transactions);
    return moveInCosts;
  };

  componentDidMount() {
    this.props.requestRenterStatus();
    this.maybeRedirectToYourRental();
  }

  componentDidUpdate(prevProps: HomePageProps) {
    // requestStatusChanged catches a genuinely-notStarted renter, whose API
    // response matches the Redux initial default — onboardingStatus stays the
    // same but requestStatus transitions null → SUCCESS.
    const statusChanged = prevProps.onboardingStatus !== this.props.onboardingStatus;
    const locationChanged = prevProps.location?.pathname !== this.props.location?.pathname;
    const redirectUrlChanged = prevProps.redirectUrl !== this.props.redirectUrl;
    const requestStatusChanged = prevProps.requestStatus !== this.props.requestStatus;
    if (statusChanged || locationChanged || redirectUrlChanged || requestStatusChanged) {
      this.maybeRedirectToYourRental();
    }
  }

  maybeRedirectToYourRental = () => {
    const { onboardingStatus, onboardingComplete, location, fromWhere, redirectUrl, paymentDetails, requestStatus } =
      this.props;
    if (fromWhere === 'make-a-payment' && redirectUrl) {
      window.location.href = redirectUrl;
      return;
    }
    // Wait for getRenterStatus to resolve. The Redux initial state seeds
    // onboardingStatus with "notStarted", so otherwise every direct hit on
    // bare /renter-hub/payments redirects on mount before the API returns.
    if (requestStatus !== REQUEST_STATUS.SUCCESS) {
      return;
    }
    if (
      shouldRedirectToYourRental({
        onboardingStatus,
        onboardingComplete,
        paymentMethodStatus: paymentDetails?.paymentMethodStatus,
        location,
      })
    ) {
      window.location.href = paths.yourRentalPage;
    }
  };

  BaseRoute = ({ showDashboard = false, showInvitePage = false }) => (
    <RouteWithAuth
      path={paths.baseUrl}
      exact
      render={(routeProps) => {
        const searchString = this.props.location.search;
        const queryParams = queryString.parse(searchString);
        const inviteToken = queryParams.invite;
        if (showInvitePage && inviteToken) {
          return <ConfirmRentDetailsPage {...routeProps} {...this.pageProps} inviteToken={inviteToken} />;
        }
        if (showDashboard) {
          this.schedulePaymentFlow = false;
          return <RenterDashboardV2 {...routeProps} {...this.pageProps} />;
        }
        return <LandingPage {...routeProps} {...this.pageProps} authenticated />;
      }}
    />
  );

  PaymentReportingOptInRoute = (fromDashboard: boolean) => {
    const path = fromDashboard ? paths.editPaymentReportingEnrollmentPage : paths.paymentReportingOptInPage;
    return (
      <RouteWithAuth
        path={paths.baseUrl + path}
        exact
        render={(routeProps) => (
          <PaymentReportingOptInPage {...routeProps} {...this.pageProps} fromDashboard={fromDashboard} />
        )}
      />
    );
  };

  PaymentInfoRoute = () => (
    <RouteWithAuth
      path={paths.baseUrl + paths.paymentInfoPage}
      exact
      render={(routeProps) => <PaymentInfoPageV2 {...routeProps} {...this.pageProps} />}
    />
  );

  PaymentSettingsRoute = () => (
    <RouteWithAuth
      path={paths.baseUrl + paths.paymentSettingsPage}
      exact
      render={(routeProps) => <PaymentSettingsPageV2 {...routeProps} {...this.pageProps} />}
    />
  );

  UpdatePaymentMethodsRoute = () => (
    <RouteWithAuth
      path={paths.baseUrl + paths.updatePaymentMethodsPage}
      exact
      render={(routeProps) => <UpdatePaymentMethodsPage fromDashboard {...routeProps} {...this.pageProps} />}
    />
  );

  PlaidConnectRoute = () => (
    <RouteWithAuth
      path={paths.baseUrl + paths.plaidConnectPage}
      exact
      render={(routeProps) => <PlaidConnectPage {...routeProps} {...this.pageProps} />}
    />
  );

  // Legacy redirect for old emails that link to /plaid-micro-deposits
  PlaidMicroDepositsLegacyRedirect = () => (
    <Route
      path={paths.baseUrl + '/plaid-micro-deposits'}
      exact
      render={() => <Redirect to={paths.baseUrl + paths.plaidConnectPage + '?action=verify'} />}
    />
  );

  ConfirmNewRentAmountRoute = () => <RouteWithAuth path={paths.baseUrl} exact render={() => <ConfirmNewRentPage />} />;

  EditPaymentInfoRoute = () => (
    <RouteWithAuth
      path={paths.baseUrl + paths.editPaymentInfoPage}
      exact
      render={(routeProps) => {
        const fromWhere = routeProps.location.state && routeProps.location.state.fromWhere;
        return (
          <>
            {fromWhere === paths.plaidConnectPage ? (
              <PlaidConnectPage {...routeProps} {...this.pageProps} />
            ) : (
              <RenterDashboardV2 {...routeProps} {...this.pageProps} />
            )}
            <PaymentInfoPageV2 {...routeProps} {...this.pageProps} fromDashboard />
          </>
        );
      }}
    />
  );

  EditPaymentSettingsRoute = () => (
    <RouteWithAuth
      path={paths.baseUrl + paths.editPaymentSettingsPage}
      exact
      render={(routeProps) => (
        <>
          <RenterDashboardV2 {...routeProps} {...this.pageProps} />
          <PaymentSettingsPageV2 {...routeProps} {...this.pageProps} fromDashboard />
        </>
      )}
    />
  );

  ScheduleMoveInCostsRoute = () => (
    <RouteWithAuth
      path={paths.baseUrl + paths.moveInCostsSchedulingPage}
      exact
      render={(routeProps) => <MoveInCostsSchedulingPage {...routeProps} {...this.pageProps} />}
    />
  );

  EditMoveInCostsRoute = () => (
    <RouteWithAuth
      path={paths.baseUrl + paths.editMoveInCostsSchedulePage}
      exact
      render={(routeProps) => {
        const searchString = this.props.location.search;
        const queryParams = queryString.parse(searchString);
        const { failedMICPaymentEmail } = queryParams;
        return (
          <>
            <RenterDashboardV2 {...routeProps} {...this.pageProps} />
            <MoveInCostsSchedulingPage
              {...routeProps}
              {...this.pageProps}
              fromDashboard
              failedPaymentEmail={failedMICPaymentEmail}
            />
          </>
        );
      }}
    />
  );

  ManagePaymentMethodsRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={paths.baseUrl + paths.managePaymentMethodsPage}
      exact
      render={(routeProps) => {
        return <ManagePaymentMethodsPage {...routeProps} {...this.pageProps} fromDashboard />;
      }}
    />
  );

  PayOneTimePaymentRoute = () => {
    const { unpaidOtpIndexes = [] } = this.props;
    return (
      <RouteWithAuth
        path={paths.baseUrl + paths.oneTimePaymentPage}
        exact
        render={(routeProps) => {
          const searchString = this.props.location.search;
          const queryParams = queryString.parse(searchString);
          const { failedPaymentEmail, otpKey } = queryParams;
          if (otpKey && unpaidOtpIndexes.indexOf(String(otpKey)) !== -1) {
            return (
              <PayOneTimePaymentPage
                {...routeProps}
                {...this.pageProps}
                otpKey={otpKey}
                failedPaymentEmail={failedPaymentEmail}
              />
            );
          }
          return <Redirect to={{ pathname: paths.baseUrl, search: '' }} />;
        }}
      />
    );
  };

  CancelPaymentRoute = () => (
    <RouteWithAuth
      path={paths.baseUrl + paths.cancelPaymentPage}
      exact
      render={(routeProps) => (
        <>
          <RenterDashboardV2 {...routeProps} {...this.pageProps} />
          <CancelPaymentPage />
        </>
      )}
    />
  );

  CancelMoveInCostsRoute = () => (
    <RouteWithAuth
      path={paths.baseUrl + paths.cancelMoveInCostsPage}
      exact
      render={(routeProps) => (
        <>
          <RenterDashboardV2 {...routeProps} {...this.pageProps} />
          <CancelPaymentPage isMoveInCosts />
        </>
      )}
    />
  );

  PayPastDueRentPaymentRoute = () => {
    const { pastDueRentPaymentIndexes = [] } = this.props;
    return (
      <RouteWithAuth
        path={paths.baseUrl + paths.payPastDueRentPaymentPage}
        exact
        render={(routeProps) => {
          const searchString = this.props.location.search;
          const queryParams = queryString.parse(searchString);
          const { failedPaymentEmail, scheduleMonth } = queryParams;
          if (scheduleMonth && pastDueRentPaymentIndexes.indexOf(String(scheduleMonth)) !== -1) {
            return (
              <>
                <RenterDashboardV2 {...routeProps} {...this.pageProps} />
                <PayPastDueRentPaymentPage
                  {...routeProps}
                  {...this.pageProps}
                  failedPaymentEmail={failedPaymentEmail}
                  scheduleMonth={scheduleMonth}
                />
              </>
            );
          }
          return <Redirect to={{ pathname: paths.baseUrl, search: '' }} />;
        }}
      />
    );
  };

  RentReportingRoute = () => (
    <RouteWithAuth
      path={paths.baseUrl + paths.rentReportingPage}
      exact
      render={(routeProps) => {
        const searchString = this.props.location.search;
        const queryParams = queryString.parse(searchString);
        const autoOpenCreditTrackingEnroll = queryParams.autoOpenEnroll;
        return (
          <RenterDashboardV2
            {...routeProps}
            {...this.pageProps}
            autoOpenCreditTrackingEnroll={autoOpenCreditTrackingEnroll}
          />
        );
      }}
    />
  );

  CatchAllRoute = ({ redirectTo = paths.baseUrl } = { redirectTo: paths.baseUrl }) => (
    <Route path="*" render={() => <Redirect to={{ pathname: redirectTo, search: '' }} />} />
  );

  renderNotStartedRoutes = () => (
    <Switch location={this.props.location}>
      {this.BaseRoute({ showDashboard: false, showInvitePage: true })}
      {this.CatchAllRoute()}
    </Switch>
  );

  renderRentAmountChangeRoutes = () => (
    <Switch location={this.props.location}>
      {this.ConfirmNewRentAmountRoute()}
      {this.CatchAllRoute()}
    </Switch>
  );

  renderExternalAccountMissing = () => (
    <Switch location={this.props.location}>
      {this.BaseRoute({ showDashboard: false, showInvitePage: false })}
      {this.PaymentReportingOptInRoute(false)}
      {this.PaymentInfoRoute()}
      {this.PlaidConnectRoute()}
      {this.PlaidMicroDepositsLegacyRedirect()}
      {this.isMoveInCostsPresent() && this.ScheduleMoveInCostsRoute()}
      {this.PaymentSettingsRoute()}
      {this.RentReportingRoute()}
      {this.CatchAllRoute({
        redirectTo: paths.baseUrl + paths.paymentInfoPage,
      })}
    </Switch>
  );

  renderTermCompleteRoutes = () => {
    return (
      <Switch location={this.props.location}>
        {this.BaseRoute({ showDashboard: true, showInvitePage: true })}
        {this.PaymentInfoRoute()}
        {this.EditPaymentInfoRoute()}
        {this.PlaidConnectRoute()}
        {this.PlaidMicroDepositsLegacyRedirect()}
        {this.UpdatePaymentMethodsRoute()}
        {this.ManagePaymentMethodsRoute()}
        {this.EditPaymentSettingsRoute()}
        {this.PayPastDueRentPaymentRoute()}
        {this.PayOneTimePaymentRoute()}
        {this.EditMoveInCostsRoute()}
        {this.RentReportingRoute()}
        {this.CatchAllRoute()}
      </Switch>
    );
  };

  renderBankVerificationNeededRoutes = (paymentMethodStatus) => {
    const currentPath = this.props.location?.pathname;
    const plaidConnectPath = paths.baseUrl + paths.plaidConnectPage;
    const editPaymentInfoPath = paths.baseUrl + paths.editPaymentInfoPage;
    const legacyMicroDepositsPath = paths.baseUrl + '/plaid-micro-deposits';

    // If not at a valid path for this route set, redirect first
    const isAtValidPath =
      currentPath === plaidConnectPath ||
      currentPath === editPaymentInfoPath ||
      currentPath === legacyMicroDepositsPath;

    if (!isAtValidPath) {
      const redirectTo =
        paymentMethodStatus === VERIFICATION_STATUS.PENDING_AUTOMATIC.INTERNAL ||
        paymentMethodStatus === VERIFICATION_STATUS.PENDING_MANUAL.INTERNAL ||
        paymentMethodStatus === VERIFICATION_STATUS.VERIFICATION_FAILED
          ? `${paths.baseUrl}${paths.plaidConnectPage}?action=verify`
          : paths.baseUrl;
      return <Redirect to={redirectTo} />;
    }

    return (
      <Switch location={this.props.location}>
        {this.EditPaymentInfoRoute()}
        {this.PlaidConnectRoute()}
        {this.PlaidMicroDepositsLegacyRedirect()}
      </Switch>
    );
  };

  renderPaymentMethodSetupRoutes = () => (
    <Switch location={this.props.location}>
      {this.BaseRoute({ showDashboard: true, showInvitePage: false })}
      {this.PaymentInfoRoute()}
      {this.PlaidConnectRoute()}
      {this.PlaidMicroDepositsLegacyRedirect()}
      {this.isMoveInCostsPresent() && this.ScheduleMoveInCostsRoute()}
      {this.PaymentSettingsRoute()}
      {this.RentReportingRoute()}
      {this.CatchAllRoute({
        redirectTo: paths.baseUrl + paths.paymentSettingsPage,
      })}
    </Switch>
  );

  renderActiveRenterRoutes = () => {
    this.schedulePaymentFlow = false;
    const { nextPayable, paymentDetails, moveInCostsDetails } = this.props;
    // A renter can cancel a payment if there is a scheduled payment, or if there is a payment subscription.
    const paymentIsCancellable =
      (nextPayable.dueDate && nextPayable.isScheduled) ||
      (paymentDetails.paymentSubscriptionDetails && paymentDetails.paymentSubscriptionDetails.recurring);
    const isMoveInCostsCancellable =
      moveInCostsDetails.isScheduled && moveInCostsDetails.status !== TransactionConstants.STATUS.COMPLETE;
    return (
      <Switch location={this.props.location}>
        {this.BaseRoute({ showDashboard: true, showInvitePage: false })}
        {this.EditPaymentInfoRoute()}
        {this.PlaidConnectRoute()}
        {this.PlaidMicroDepositsLegacyRedirect()}
        {this.UpdatePaymentMethodsRoute()}
        {this.PaymentReportingOptInRoute(true)}
        {this.EditPaymentSettingsRoute()}
        {this.ManagePaymentMethodsRoute()}
        {paymentIsCancellable && this.CancelPaymentRoute()}
        {isMoveInCostsCancellable && this.CancelMoveInCostsRoute()}
        {this.EditMoveInCostsRoute()}
        {this.PayOneTimePaymentRoute()}
        {this.PayPastDueRentPaymentRoute()}
        {this.RentReportingRoute()}
        {this.CatchAllRoute()}
      </Switch>
    );
  };

  renderOnHoldRoutes = () => {
    const { onboardingComplete } = this.props;
    const showDashboard = onboardingComplete || this.landingPageVisited;
    this.landingPageVisited = true;
    return (
      <Switch location={this.props.location}>
        {this.BaseRoute({ showDashboard, showInvitePage: true })}
        {this.CatchAllRoute()}
      </Switch>
    );
  };

  renderBody() {
    const {
      onboardingStatus,
      rentTerms,
      bankAccountVerificationNeeded,
      ignoreRentAmountChange,
      nextPayable,
      paymentDetails,
      redirectUrl,
      fromWhere,
    } = this.props;

    const { rentAmountChangeRequest } = rentTerms;

    if (fromWhere === 'make-a-payment' && redirectUrl) {
      return <HomePageSkeleton />;
    }

    // TM-952: bare /renter-hub/payments visits for renters without a valid payment method
    // land on Your Rental instead of the legacy splash page. The redirect itself is fired
    // from componentDidMount/componentDidUpdate — keep render pure and show the skeleton
    // while the browser navigates away.
    if (
      shouldRedirectToYourRental({
        onboardingStatus,
        onboardingComplete: this.props.onboardingComplete,
        paymentMethodStatus: paymentDetails?.paymentMethodStatus,
        location: this.props.location,
      })
    ) {
      return <HomePageSkeleton />;
    }

    // Show not-started renters/renters with expired term the invite page if valid token, else show the landing page
    if (onboardingStatus === OnboardingStatus.NOT_STARTED || onboardingStatus === OnboardingStatus.TERM_EXPIRED) {
      return this.renderNotStartedRoutes();
    }

    // Show the "blank" dashboard for a renter if a landlord is blocked
    if (onboardingStatus === OnboardingStatus.REJECTED) {
      return this.renderOnHoldRoutes();
    }

    // Send user to payment method setup, if it isn't already saved
    if (
      (onboardingStatus === OnboardingStatus.REGISTERED ||
        onboardingStatus === OnboardingStatus.NO_EXTERNAL_ACCOUNT_FOUND) &&
      paymentDetails.paymentMethodStatus !== VERIFICATION_STATUS.PENDING_AUTOMATIC.INTERNAL &&
      paymentDetails.paymentMethodStatus !== VERIFICATION_STATUS.PENDING_MANUAL.INTERNAL
    ) {
      this.schedulePaymentFlow = true;
      return this.renderExternalAccountMissing();
    }

    // Handle bank verification for multiple payment methods
    const { hasPaymentMethodNeedingVerification, hasVerifiedPaymentMethod } = getPaymentMethodStatuses(
      this.props.paymentMethods,
    );

    if (hasPaymentMethodNeedingVerification && !hasVerifiedPaymentMethod) {
      // Only get bank accounts that need verification
      const bankAccountsNeedingVerification = [...this.props.paymentMethods]
        .filter(
          (pm) =>
            pm.accountType === 'bank_account' &&
            pm.isBankAccountVerificationNeeded &&
            pm.paymentMethodStatus !== 'verified',
        )
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

      if (bankAccountsNeedingVerification.length > 0) {
        const mostRecentAccountNeedingVerification = bankAccountsNeedingVerification[0];
        return this.renderBankVerificationNeededRoutes(mostRecentAccountNeedingVerification.paymentMethodStatus);
      }
    }

    // send user to the payment scheduling page
    if (this.schedulePaymentFlow && !nextPayable.isScheduled) {
      return this.renderPaymentMethodSetupRoutes();
    }

    // Show term-complete renters with an invite token the invite page
    if (onboardingStatus === OnboardingStatus.TERM_COMPLETE) {
      return this.renderTermCompleteRoutes();
    }

    // Show renters with a pending rent amount change request the rent amount change page
    if (
      rentAmountChangeRequest &&
      rentAmountChangeRequest.expired === false &&
      (rentAmountChangeRequest.renterAcceptanceDate === '' || rentAmountChangeRequest.renterAcceptanceDate === null) &&
      !ignoreRentAmountChange
    ) {
      return this.renderRentAmountChangeRoutes();
    }

    // Show active-term renters the dashboard and allow access to payment method and scheduling page
    return this.renderActiveRenterRoutes();
  }

  render() {
    if (this.props.requestStatus !== REQUEST_STATUS.SUCCESS) {
      return <HomePageSkeleton />;
    }
    return this.renderBody();
  }
}

export default HomePage;
